import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Ruler, Snowflake, LayoutGrid, MessageCircle, Umbrella, Sun } from "lucide-react";
import CtaBanner from "@/components/CtaBanner";
import Reveal from "@/components/Reveal";
import { usePageContent, useContentList, useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import useSeo from "@/lib/useSeo";

const GALLERY_FALLBACK_ALFA = [
    { url: "/brand/alfa-hero.jpg", alt: "Modelo Alfa vista completa" },
    { url: "/brand/alfa-product.jpg", alt: "Modelo Alfa product shot" },
];

const NO_CANOPY_ALFA = [
    { url: "/brand/alfa-no-canopy.jpg", alt: "Modelo Alfa sem cobertura" },
    { url: "/brand/alfa-product-no-canopy.jpg", alt: "Modelo Alfa sem cobertura — product shot" },
];

export default function ModeloAlfa() {
    const { t } = usePageContent("modelo_alfa");
    const { tr, fmtPrice } = useLocale();
    const gallery = useContentList("alfa_gallery", GALLERY_FALLBACK_ALFA, { forceFallbackEn: false });
    const images = gallery.map((g) => g.url).filter(Boolean);
    const mainImg = t("hero", "main_image", "");
    const finalImages = mainImg && !images.includes(mainImg) ? [mainImg, ...images] : (images.length ? images : [mainImg || "/brand/alfa-hero.jpg"]);

    useSeo({
        title: tr("Modelo Alfa — Food cart compacto premium", "Alfa Model — Premium compact food cart"),
        description: tr(
            "Carrinho compacto NOVIFOOD Alfa: 1,35 m, 0,90 m de bancada, ideal para single-product (gelados, crepes, hot dogs). Energia solar opcional, branding à medida. Desde €2.000.",
            "NOVIFOOD Alfa compact cart: 1.35 m, 0.90 m counter, ideal for single-product (ice cream, crêpes, hot dogs). Optional solar power, custom branding. From €2,000."
        ),
        path: "/modelo-alfa",
        type: "product",
        image: mainImg || "/brand/alfa-hero.jpg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "NOVIFOOD Modelo Alfa",
            "description": "Carrinho compacto NOVIFOOD Alfa: 1,35 m, ideal para single-product. Energia solar opcional.",
            "brand": { "@type": "Brand", "name": "NOVIFOOD" },
            "offers": {
                "@type": "Offer",
                "priceCurrency": "EUR",
                "price": "2000",
                "availability": "https://schema.org/InStock",
                "url": "https://novifood.pt/modelo-alfa",
            },
        },
    });

    return (
        <ProductPage
            pageId="modelo_alfa"
            name={t("hero", "eyebrow", tr("Modelo Alfa", "Alfa Model"))}
            title={t("hero", "title", tr("Compacto, eficiente, premium.", "Compact, efficient, premium."))}
            tag={t("hero", "eyebrow", tr("Modelo Alfa", "Alfa Model"))}
            price={fmtPrice(2000)}
            delivery={t("hero", "delivery", tr("Entrega em 30 dias", "Delivery in 30 days"))}
            warranty={t("hero", "warranty", tr("Garantia 2 anos", "2-year warranty"))}
            tagline={t("hero", "subtitle", tr("A ilha funcional certa para começar com investimento reduzido.", "The right functional island to start with a low investment."))}
            ctaLabel={t("hero", "cta_label", tr("Pedir orçamento", "Request a quote"))}
            ctaTo={t("hero", "cta_to", "/contacto")}
            images={finalImages}
            imagesNoCanopy={NO_CANOPY_ALFA.map((g) => g.url)}
            specs={[
                { icon: Ruler, title: tr("Tamanho", "Size"), text: t("specs", "size", "1,35 m × 0,90 m") },
                { icon: Ruler, title: tr("Peso", "Weight"), text: t("specs", "weight", "~110 kg") },
                { icon: Snowflake, title: tr("Energia", "Energy"), text: t("specs", "energy", tr("Solar + bateria 1 kWh", "Solar + 1 kWh battery")) },
                { icon: Snowflake, title: tr("Refrigeração", "Cooling"), text: t("specs", "cooling", tr("Compartimento isotérmico opcional", "Optional isothermal compartment")) },
                { icon: LayoutGrid, title: tr("Estrutura", "Structure"), text: t("specs", "structure", tr("Alumínio reforçado · pintura epoxy", "Reinforced aluminium · epoxy coating")) },
            ]}
        />
    );
}

export function ProductPage({ pageId, name, title, tag, price, tagline, images, imagesNoCanopy, ctaLabel, ctaTo, delivery, warranty, specs }) {
    const [active, setActive] = useState(0);
    const [canopyOn, setCanopyOn] = useState(true);
    const { tr } = useLocale();
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");
    const hasNoCanopy = Array.isArray(imagesNoCanopy) && imagesNoCanopy.length > 0;
    const displayImages = canopyOn || !hasNoCanopy ? images : imagesNoCanopy;
    const safeActive = Math.min(active, Math.max(0, displayImages.length - 1));
    const activeImg = displayImages[safeActive] || displayImages[0];

    return (
        <>
            <section className="container-x pt-6 pb-8 lg:pt-8 lg:pb-10" data-testid={`product-hero-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        {hasNoCanopy && (
                            <div className="mb-3 inline-flex rounded-full border border-border bg-white p-1 shadow-sm" role="tablist" aria-label={tr("Cobertura do carrinho", "Cart canopy")} data-testid="canopy-toggle">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={canopyOn}
                                    onClick={() => { setCanopyOn(true); setActive(0); }}
                                    data-testid="canopy-toggle-on"
                                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                                        canopyOn ? "bg-primary text-primary-foreground shadow-[0_4px_12px_-4px_rgba(11,37,69,0.4)]" : "text-muted-foreground hover:text-primary"
                                    }`}
                                >
                                    <Umbrella className="h-3.5 w-3.5" strokeWidth={2} />
                                    {tr("Com cobertura", "With canopy")}
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={!canopyOn}
                                    onClick={() => { setCanopyOn(false); setActive(0); }}
                                    data-testid="canopy-toggle-off"
                                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                                        !canopyOn ? "bg-primary text-primary-foreground shadow-[0_4px_12px_-4px_rgba(11,37,69,0.4)]" : "text-muted-foreground hover:text-primary"
                                    }`}
                                >
                                    <Sun className="h-3.5 w-3.5" strokeWidth={2} />
                                    {tr("Sem cobertura", "Open top")}
                                </button>
                            </div>
                        )}
                        <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-muted shadow-[0_20px_40px_-16px_rgba(11,37,69,0.15)]">
                            <img src={activeImg} alt={name}
                                className="h-full w-full object-contain transition duration-1000 hover:scale-[1.04]"
                                data-testid="product-main-image" />
                        </div>
                        {displayImages.length > 1 && (
                            <div className="mt-3 grid grid-cols-5 gap-2">
                                {displayImages.map((src, i) => (
                                    <button
                                        key={src}
                                        type="button"
                                        onClick={() => setActive(i)}
                                        data-testid={`product-thumb-${i}`}
                                        className={`aspect-[4/3] overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                                            safeActive === i ? "border-secondary shadow-md" : "border-transparent opacity-70 hover:opacity-100 hover:border-border"
                                        }`}
                                    >
                                        <img src={src} alt="" className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <span className="eyebrow-line">{tr("Produto", "Product")}</span>
                        <h1 className="mt-2 display-lg text-primary">{title || name}</h1>
                        <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">{tagline}</p>

                        <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                            <span className="font-heading text-4xl font-extrabold text-primary">
                                <span className="serif-accent">{price}</span>
                            </span>
                            <span className="text-xs text-muted-foreground">
                                <span className="font-bold uppercase tracking-wider">{tr("s/IVA", "excl. VAT")}</span>
                                <span className="ml-1">· {tr("IVA (23%) adicionado no checkout", "VAT (23%) added at checkout")}</span>
                            </span>
                        </div>

                        <ul className="mt-4 space-y-2">
                            {[delivery, warranty].filter(Boolean).map((b) => (
                                <li key={b} className="flex items-start gap-2 text-xs sm:text-sm text-primary/90">
                                    <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                                    </span>
                                    {b}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-5">
                            <Link
                                to={ctaTo || "/contacto"}
                                data-testid="product-cta-order"
                                className="btn-premium-gold"
                            >
                                {ctaLabel || tr("Peça o seu", "Get yours")} <ArrowRight className="h-4 w-4 arrow-shift" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-muted/60 border-y border-border" data-testid={`product-specs-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="container-x section-y">
                    <Reveal className="max-w-2xl">
                        <span className="eyebrow-line">{tr("Especificações", "Specifications")}</span>
                        <h2 className="mt-5 display-lg text-primary">
                            {tr("Construído para ", "Built to ")}<span className="serif-accent text-primary">{tr("durar", "last")}</span>{tr(", pensado para operar.", ", designed to operate.")}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {specs.map((s, i) => (
                            <Reveal key={s.title} delay={Math.min((i % 3) + 1, 4)}>
                                <article className="h-full rounded-2xl bg-white border border-border p-7 lg:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30" data-testid={`spec-${s.title.toLowerCase()}`}>
                                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                                        <s.icon className="h-5 w-5" strokeWidth={1.75} />
                                    </div>
                                    <h3 className="mt-6 font-heading text-2xl lg:text-[26px] font-bold text-primary leading-tight">{s.title}</h3>
                                    <p className="mt-3 text-sm lg:text-base text-muted-foreground leading-relaxed">{s.text}</p>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-x pt-6 pb-6 lg:pt-12 lg:pb-12" data-testid={`product-cta-banner-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}>
                <CtaBanner
                    eyebrow={tr(`Pronto para o ${name}?`, `Ready for the ${name}?`)}
                    title={tr("Receba uma proposta personalizada em 48h.", "Get a personalised proposal within 48h.")}
                    description={tr(
                        "Configure cores, equipamentos e cobertura — ou fale directamente com a nossa equipa para um briefing dedicado ao seu negócio.",
                        "Configure colours, equipment and canopy — or talk directly to our team for a briefing dedicated to your business."
                    )}
                    primary={{ to: "/simulador", label: tr("Configurar no simulador", "Configure it yourself") }}
                    secondary={{ to: `https://wa.me/${whatsappNumber}`, label: tr("Falar no WhatsApp", "Chat on WhatsApp"), external: true, icon: MessageCircle }}
                    variant="navy"
                    testId={`product-cta-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}
                />
            </section>

            <ProductFooterNav name={name} />
        </>
    );
}

function ProductFooterNav({ name }) {
    const { tr, fmtPrice } = useLocale();
    const isAlfa = (name || "").toLowerCase().includes("alfa");
    const prev = isAlfa ? null : { to: "/modelo-alfa", label: tr("Modelo Alfa", "Alfa Model"), price: fmtPrice(2000) };
    const next = isAlfa ? { to: "/modelo-omega", label: tr("Modelo Ômega", "Omega Model"), price: fmtPrice(3000) } : null;
    return (
        <section className="container-x py-10 lg:py-14" data-testid="product-footer-nav">
            <div className="grid gap-6 md:grid-cols-2">
                {prev ? (
                    <Link to={prev.to} data-testid="product-prev" className="group rounded-3xl border border-border bg-white p-7 lg:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30">
                        <span className="text-xs font-bold uppercase tracking-[0.22em] text-muted-foreground">← {tr("Modelo anterior", "Previous model")}</span>
                        <p className="mt-3 font-heading text-3xl font-extrabold text-primary">{prev.label}</p>
                        <p className="text-sm text-muted-foreground mt-2">{tr("desde", "from")} <span className="text-primary font-semibold serif-accent">{prev.price}</span></p>
                    </Link>
                ) : <div />}
                {next ? (
                    <Link to={next.to} data-testid="product-next" className="group rounded-3xl border border-primary bg-primary text-primary-foreground p-7 lg:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.4)] md:text-right">
                        <span className="text-xs font-bold uppercase tracking-[0.22em] text-secondary">{tr("Modelo seguinte", "Next model")} →</span>
                        <p className="mt-3 font-heading text-3xl font-extrabold">{next.label}</p>
                        <p className="text-sm text-white/70 mt-2">{tr("desde", "from")} <span className="text-secondary font-semibold serif-accent">{next.price}</span></p>
                    </Link>
                ) : <div />}
            </div>
        </section>
    );
}
